import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '@/shared/api/client.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Field } from '@/shared/ui/field.tsx'
import { Alert } from '@/shared/ui/states.tsx'
import { cn } from '@/shared/lib/cn.ts'
import { useLogin } from './use-session.ts'

const SUPPORT_EMAIL = 'support@manaro.io'

export function LoginForm({ redirectTo }: { redirectTo: string }) {
  const navigate = useNavigate()
  const { mutateAsync, isPending } = useLogin()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [needsHelp, setNeedsHelp] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)

    if (!email.includes('@')) {
      setError('Проверьте почту — кажется, в адресе опечатка.')
      return
    }
    if (password.length < 6) {
      setError('Неверная почта или пароль. Попробуйте ещё раз.')
      return
    }

    try {
      await mutateAsync({ email, password, remember })
      navigate(redirectTo, { replace: true })
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не удалось войти. Попробуйте ещё раз.')
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4.5" noValidate>
      <div>
        <h1 className="mb-1.5 font-display text-2xl font-semibold tracking-[-0.015em]">Вход в админку</h1>
        <p className="text-[14px] text-ink-muted text-balance-pretty">
          Рабочая почта и пароль. Доступ выдаёт владелец сервиса.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      <Field
        label="Почта"
        type="email"
        autoComplete="email"
        placeholder="anna@manaro.io"
        value={email}
        autoFocus
        onChange={(event) => {
          setEmail(event.target.value)
          setError(null)
        }}
      />

      <Field
        label="Пароль"
        type={showPassword ? 'text' : 'password'}
        autoComplete="current-password"
        placeholder="••••••••"
        value={password}
        onChange={(event) => {
          setPassword(event.target.value)
          setError(null)
        }}
        action={
          <button
            type="button"
            onClick={() => setShowPassword((value) => !value)}
            className="text-[12.5px] text-brand hover:text-brand-dark"
          >
            {showPassword ? 'Скрыть' : 'Показать'}
          </button>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <button
          type="button"
          onClick={() => setRemember((value) => !value)}
          aria-pressed={remember}
          className="flex items-center gap-2.5 text-[13.5px] text-ink-soft"
        >
          <span
            className={cn(
              'flex size-[18px] shrink-0 items-center justify-center rounded-md border transition-colors',
              remember ? 'border-brand bg-brand' : 'border-line-dashed bg-surface',
            )}
          >
            {remember && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="m5 13 4 4 10-10" />
              </svg>
            )}
          </span>
          Не выходить
        </button>
        <button
          type="button"
          onClick={() => setNeedsHelp((value) => !value)}
          className="text-[13.5px] text-brand hover:text-brand-dark"
        >
          Забыли пароль?
        </button>
      </div>

      <Button type="submit" loading={isPending}>
        {isPending ? 'Проверяем…' : 'Войти'}
      </Button>

      {needsHelp && (
        <div className="rounded-control border border-line-soft bg-canvas px-3.5 py-3 text-[12.5px] text-ink-muted text-balance-pretty">
          Пароли выдаёт владелец сервиса — самостоятельного восстановления в админке нет. Напишите на{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-brand hover:text-brand-dark">
            {SUPPORT_EMAIL}
          </a>
          , и вам пришлют новый.
        </div>
      )}
    </form>
  )
}
